import { NavLink } from "react-router-dom"
import { navIconMap, CollectionsIcon, LogoutIcon, XMarkIcon } from "../icons"
import { Badge } from "./badge"

interface NavItem {
  key: string
  label: string
  path: string
  section?: string
}

export function AppSidebar({
  navigation,
  badgeCounts,
  user,
  onLogout,
  onNavigate,
  mobile = false,
}: {
  navigation: NavItem[]
  badgeCounts?: Record<string, number>
  user: { fullName: string; role: string }
  onLogout: () => void
  onNavigate?: () => void
  mobile?: boolean
}) {
  const sectionMap = new Map<string, NavItem[]>()
  const sectionOrder: string[] = []

  for (const item of navigation) {
    const key = item.section ?? ""
    if (!sectionMap.has(key)) {
      sectionMap.set(key, [])
      sectionOrder.push(key)
    }
    sectionMap.get(key)!.push(item)
  }

  const sections = sectionOrder.map((key) => ({
    title: key || undefined,
    items: sectionMap.get(key)!,
  }))

  return (
    <aside
      className={`flex h-full min-h-0 w-full flex-col bg-slate-900 text-slate-300 ${
        mobile ? "rounded-[1.75rem] border border-slate-800 shadow-2xl" : "border-r border-slate-800"
      }`}
    >
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-4 sm:px-6">
        <div className="flex items-center space-x-3">
          <div className="rounded-lg bg-blue-600 p-2">
            <CollectionsIcon className="h-6 w-6 text-white" />
          </div>
          <div>
            <span className="block font-bold text-xl tracking-tight text-white">LoanCore</span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              Admin
            </span>
          </div>
        </div>
        {mobile ? (
          <button
            type="button"
            aria-label="Close navigation menu"
            className="rounded-full border border-slate-700 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
            onClick={onNavigate}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        ) : null}
      </div>

      <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto px-3 py-4 scrollbar-thin">
        {sections.map((section, index) => (
          <div key={section.title ?? index} className={index > 0 ? "mt-2 border-t border-slate-700/50 pt-1" : ""}>
            {section.title ? (
              <div className="px-3 pb-2 pt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {section.title}
              </div>
            ) : null}
            {section.items.map((item) => {
              const IconComp = navIconMap[item.key]
              const badge = badgeCounts?.[item.key]

              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-r-md border-l-[3px] px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                      isActive
                        ? "border-blue-500 bg-slate-800 text-white"
                        : "border-transparent text-slate-300 hover:bg-slate-800/70 hover:text-slate-100"
                    }`
                  }
                >
                  <span className="flex min-w-0 items-center">
                    {IconComp ? <IconComp className="mr-3 h-5 w-5 shrink-0" /> : null}
                    <span className="truncate">{item.label}</span>
                  </span>
                  {badge ? <Badge color="red">{badge}</Badge> : null}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>

      <div className="border-t border-slate-700/50 px-4 py-5">
        <div className="flex items-center space-x-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-700 text-xs font-bold text-white">
            {user.fullName
              .split(" ")
              .map((name) => name[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium text-white">{user.fullName}</p>
            <p className="truncate text-[10px] text-slate-500">{user.role.replace(/_/g, " ")}</p>
          </div>
          <button
            type="button"
            title="Sign out"
            className="text-slate-500 transition-colors hover:text-white"
            onClick={onLogout}
          >
            <LogoutIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  )
}
